import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X, Download, Mail, Calendar, DollarSign, FileText } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Button } from './ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

/**
 * TransactionHistoryModal Component
 * Displays user's transaction history with option to send receipts
 * 
 * @param {boolean} show - Whether modal is visible
 * @param {function} onClose - Callback to close modal
 * @param {string} serviceFilter - Optional service filter (BVADM, MMG, BOCP, BCTG)
 */
const TransactionHistoryModal = ({ show, onClose, serviceFilter = null }) => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sendingReceipt, setSendingReceipt] = useState(null);
  const contentRef = React.useRef(null);

  useEffect(() => {
    if (show) {
      loadTransactions();
      // Scroll to top of content area when modal opens, with slight delay to ensure content is rendered
      setTimeout(() => {
        if (contentRef.current) {
          contentRef.current.scrollTop = 0;
        }
      }, 100);
    }
  }, [show, serviceFilter]);

  const loadTransactions = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/payments/transactions`, {
        withCredentials: true
      });

      let txns = response.data.transactions || [];
      
      // Filter by service if specified
      if (serviceFilter) {
        txns = txns.filter(t => t.service_name && t.service_name.includes(serviceFilter));
      }

      // Filter only successful charges
      txns = txns.filter(t => t.transaction_status === 'completed' && t.amount > 0);

      // Sort by date descending
      txns.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

      setTransactions(txns);
    } catch (error) {
      console.error('Failed to load transactions:', error);
      toast.error('Failed to load transaction history');
    } finally {
      setLoading(false);
    }
  };

  const handleSendReceipt = async (transactionId) => {
    setSendingReceipt(transactionId);
    try {
      await axios.post(
        `${BACKEND_URL}/api/payments/send-receipt`,
        { transaction_id: transactionId },
        { withCredentials: true }
      );
      toast.success('Receipt sent to your email!');
    } catch (error) {
      console.error('Failed to send receipt:', error);
      toast.error('Failed to send receipt. Please try again.');
    } finally {
      setSendingReceipt(null);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getServiceIcon = (serviceName) => {
    if (serviceName.includes('BVADM')) return '📊';
    if (serviceName.includes('MMG')) return '🏷️';
    if (serviceName.includes('BOCP')) return '📖';
    if (serviceName.includes('BCTG')) return '📚';
    if (serviceName.includes('ERMT')) return '📕';
    if (serviceName.includes('PRMPB')) return '📄';
    if (serviceName.includes('AKSW')) return '📤';
    if (serviceName.includes('IBG')) return '📊';
    if (serviceName.includes('MMKG')) return '📰';
    if (serviceName.includes('SMMK')) return '📱';
    return '💼';
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-full sm:max-w-3xl md:max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4 sm:p-6 flex justify-between items-center">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold mb-1" style={{fontFamily: "'Times New Roman', Times, serif"}}>
              Transaction History
            </h2>
            {serviceFilter && (
              <p className="text-blue-100 text-sm">
                Showing transactions for {serviceFilter}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-blue-800 rounded-full p-2 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div ref={contentRef} className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-slate-600 mt-4">Loading transactions...</p>
            </div>
          ) : transactions.length === 0 ? (
            <div className="text-center py-12">
              <FileText size={48} className="mx-auto text-slate-300 mb-4" />
              <p className="text-slate-600 text-lg">No transactions found</p>
              <p className="text-slate-400 text-xs sm:text-sm mt-2">
                {serviceFilter 
                  ? `You haven't made any paid transactions for ${serviceFilter} yet.`
                  : "You haven't made any paid transactions yet."}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {transactions.map((txn) => (
                <Card key={txn.id} className="border-slate-200 hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start gap-4">
                      {/* Left: Transaction Details */}
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="text-2xl">{getServiceIcon(txn.service_name)}</span>
                          <div>
                            <h3 className="font-bold text-slate-900" style={{fontFamily: "'Times New Roman', Times, serif"}}>
                              {txn.service_name || 'Service'}
                            </h3>
                            <p className="text-xs sm:text-sm text-slate-500">
                              {txn.book_title || 'Untitled'}
                            </p>
                          </div>
                        </div>

                        <div className="grid grid-cols-3 gap-4 mt-3 text-sm">
                          {/* Date */}
                          <div className="flex items-center gap-2 text-slate-600">
                            <Calendar size={16} className="text-slate-400" />
                            <span>{formatDate(txn.created_at)}</span>
                          </div>

                          {/* Amount */}
                          <div className="flex items-center gap-2 text-slate-600">
                            <DollarSign size={16} className="text-green-500" />
                            <span className="font-bold text-green-600">
                              {formatAmount(txn.amount)}
                            </span>
                          </div>

                          {/* Transaction ID */}
                          <div className="text-slate-500 text-xs">
                            ID: {txn.id.substring(0, 8)}...
                          </div>
                        </div>
                      </div>

                      {/* Right: Actions */}
                      <div className="flex flex-col gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleSendReceipt(txn.id)}
                          disabled={sendingReceipt === txn.id}
                          className="whitespace-nowrap"
                        >
                          {sendingReceipt === txn.id ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                              Sending...
                            </>
                          ) : (
                            <>
                              <Mail size={16} className="mr-2" />
                              Send Receipt
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-slate-50 p-4 border-t border-slate-200 flex justify-between items-center">
          <p className="text-xs sm:text-sm text-slate-600">
            Total Transactions: <span className="font-bold">{transactions.length}</span>
            {transactions.length > 0 && (
              <>
                {' | '}Total Spent: <span className="font-bold text-green-600">
                  {formatAmount(transactions.reduce((sum, t) => sum + t.amount, 0))}
                </span>
              </>
            )}
          </p>
          <Button onClick={onClose} variant="outline">
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TransactionHistoryModal;
